import Component from '@glimmer/component';
import { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import { dropTask } from 'ember-concurrency';
import { t } from 'ember-intl';
import Button from 'tio-ui/components/button';
import Chip from 'tio-ui/components/chip';
import Modal from 'tio-ui/components/modal';
import Text from 'tio-ui/components/text';
import Divider from 'tio-ui/components/divider';
import YourDetails from 'tio-employee/components/pslf/annual-auto-submit/your-details';
import { setAnnualAutoSubmitOptedOut } from 'tio-employee/utils/pslf/annual-auto-submit';
import type { AnnualAutoSubmitStatus } from 'tio-employee/utils/pslf/annual-auto-submit';
import type { PersonExtension as PersonModel } from 'tio-data/data/resources/person.ext';
import type { PslfProfileExtension as PslfProfileModel } from 'tio-data/data/resources/pslf-profile.ext';
import type PslfDebugService from 'tio-employee/services/pslf-debug';
import type { NotificationsService } from 'tio-ui/components/notifications';
import type { IntlService } from 'ember-intl';
import type { Store } from '@warp-drive/core';

export interface AnnualSettingsDialogSignature {
  Args: {
    isOpen: boolean;
    /** Called for every dismissal; the caller owns `isOpen`, and the warning panel resets with it. */
    onClose: () => void;
    /** Drives the On/Off state and the next-date copy; a null schedule reads as "not available". */
    status: AnnualAutoSubmitStatus;
    /** PATCHed by both controls — they write `annualAutoSubmitOptedOut` on this record. */
    profile: PslfProfileModel;
    /** Backs the "Your details" rows; its contact records are edited in place. */
    person: PersonModel;
  };
}

/** The only opt-out surface: a quiet link opens a comparison panel; opting back in is one click. */
export default class AnnualSettingsDialog extends Component<AnnualSettingsDialogSignature> {
  @service declare intl: IntlService;
  @service declare notifications: NotificationsService;
  @service declare pslfDebug: PslfDebugService;
  @service declare store: Store;

  @tracked isConfirmingOptOut = false;

  get status() {
    return this.args.status;
  }

  get dueDateLabel() {
    const due = this.status.dueDate;
    if (!due) return '';
    return this.intl.formatDate(due, { month: 'short', day: 'numeric', year: 'numeric' });
  }

  get explanationKey() {
    return this.status.approverKind === 'employerSigns'
      ? 'pslf.reimagined.annual_auto_submit.settings.explanation_approver'
      : 'pslf.reimagined.annual_auto_submit.settings.explanation';
  }

  close = () => {
    this.isConfirmingOptOut = false;
    this.args.onClose();
  };

  startOptOut = () => {
    this.isConfirmingOptOut = true;
  };

  keepAutoSubmit = () => {
    this.isConfirmingOptOut = false;
  };

  #reportFailure(error: unknown) {
    console.error(error);
    this.notifications.add(this.intl.t('pslf.reimagined.annual_auto_submit.save_error'), {
      appearance: 'error',
    });
  }

  optOut = dropTask(async () => {
    if (this.pslfDebug.isOverridingAnnual) {
      this.pslfDebug.setAnnualOptedOut(this.args.profile, true);
      this.isConfirmingOptOut = false;
      return;
    }

    try {
      await setAnnualAutoSubmitOptedOut(this.store, this.args.profile, true);
      this.isConfirmingOptOut = false;
    } catch (error) {
      this.#reportFailure(error);
    }
  });

  optIn = dropTask(async () => {
    if (this.pslfDebug.isOverridingAnnual) {
      this.pslfDebug.setAnnualOptedOut(this.args.profile, false);
      return;
    }

    try {
      await setAnnualAutoSubmitOptedOut(this.store, this.args.profile, false);
    } catch (error) {
      this.#reportFailure(error);
    }
  });

  <template>
    <Modal
      @isOpen={{@isOpen}}
      @onClose={{this.close}}
      @size="lg"
      @ariaLabel={{t "pslf.reimagined.annual_auto_submit.settings.heading"}}
      data-test-pslf-settings-dialog
    >
      <:header>
        {{t "pslf.reimagined.annual_auto_submit.settings.heading"}}
      </:header>
      <:body>
        <div class="tio-col">
          <YourDetails @person={{@person}} @source={{@status.source}} />

          <Divider />

          <div class="tio-row-between">
            <Text @weight="semibold">
              {{t "pslf.reimagined.annual_auto_submit.settings.toggle_label"}}
            </Text>
            {{#if @status.enrolled}}
              <Chip @intent="success" @size="sm" data-test-annual-settings-state="on">
                {{t "pslf.reimagined.annual_auto_submit.settings.state_on"}}
              </Chip>
            {{else}}
              <Chip @intent="default" @size="sm" data-test-annual-settings-state="off">
                {{t "pslf.reimagined.annual_auto_submit.settings.state_off"}}
              </Chip>
            {{/if}}
          </div>

          {{#if @status.enrolled}}
            {{#if @status.manualFormInProgress}}
              <Text @variant="muted" @size="sm" data-test-annual-settings-manual-in-progress>
                {{t "pslf.reimagined.annual_auto_submit.manual_in_progress"}}
              </Text>
            {{else}}
              <Text @variant="muted" @size="sm" data-test-annual-settings-explanation>
                {{t this.explanationKey date=this.dueDateLabel}}
              </Text>
            {{/if}}
          {{/if}}

          <Text @variant="muted" @size="sm">
            {{t "pslf.reimagined.annual_auto_submit.settings.opt_in_statement"}}
          </Text>

          {{#if @status.enrolled}}
            {{#if this.isConfirmingOptOut}}
              <div
                class="app-status-panel app-status-panel--warning p-4 tio-col-tight"
                data-test-annual-opt-out-warning
              >
                <Text @weight="semibold" @size="sm">
                  {{t "pslf.reimagined.annual_auto_submit.settings.warning_heading"}}
                </Text>
                <Text @size="sm" data-test-annual-opt-out-warning-with>
                  {{t "pslf.reimagined.annual_auto_submit.settings.warning_with"}}
                </Text>
                <Text @size="sm">
                  {{t "pslf.reimagined.annual_auto_submit.settings.warning_without"}}
                </Text>
                <div class="tio-row-tight flex-wrap mt-2">
                  <Button
                    @intent="primary"
                    @size="sm"
                    @testHook="pslf-annual-settings__keep"
                    data-test-annual-keep-auto-submit
                    {{on "click" this.keepAutoSubmit}}
                  >
                    {{t "pslf.reimagined.annual_auto_submit.settings.keep_cta"}}
                  </Button>
                  <Button
                    @appearance="link"
                    @intent="default"
                    @size="sm"
                    @isRunning={{this.optOut.isRunning}}
                    @testHook="pslf-annual-settings__opt-out-confirm"
                    data-test-annual-opt-out-confirm
                    {{on "click" this.optOut.perform}}
                  >
                    {{t "pslf.reimagined.annual_auto_submit.settings.opt_out_confirm"}}
                  </Button>
                </div>
              </div>
            {{else}}
              <div class="tio-row-end">
                <Button
                  @appearance="link"
                  @intent="default"
                  @size="sm"
                  @testHook="pslf-annual-settings__opt-out"
                  data-test-annual-opt-out-link
                  {{on "click" this.startOptOut}}
                >
                  {{t "pslf.reimagined.annual_auto_submit.settings.opt_out_link"}}
                </Button>
              </div>
            {{/if}}
          {{else}}
            <Button
              class="self-start"
              @intent="primary"
              @isRunning={{this.optIn.isRunning}}
              @testHook="pslf-annual-settings__opt-in"
              data-test-annual-opt-in
              {{on "click" this.optIn.perform}}
            >
              {{t "pslf.reimagined.annual_auto_submit.settings.opt_in_cta"}}
            </Button>
          {{/if}}
        </div>
      </:body>
    </Modal>
  </template>
}
